/**
 * Typeahead suggestions for the Anna assistant chat input.
 */
import {
  buildEntityToolQuerySuggestions,
  getToolQuerySuggestionPool,
} from './assistantToolQuerySuggestions.js';

export function inferAssistantRouteContext(route) {
  const path = String(route?.path || '').toLowerCase();
  const name = String(route?.name || '').toLowerCase();
  const hay = `${path} ${name}`;
  if (hay.includes('customer')) return 'customer';
  if (hay.includes('vendor')) return 'vendor';
  if (hay.includes('consultant')) return 'consultant';
  if (hay.includes('tracker')) return 'tracker';
  if (hay.includes('bank')) return 'banking';
  if (hay.includes('report')) return 'reports';
  if (hay.includes('journal-entry-guide') || name.includes('journalentryguide')) return 'journal-guide';
  if (hay.includes('journal')) return 'journal';
  if (hay.includes('settings') || hay.includes('accountconfiguration')) return 'settings';
  return 'general';
}

function normalizeSuggestQuery(str) {
  return String(str || '').toLowerCase().trim().replace(/\s+/g, ' ');
}

function textMatchesQuery(text, query) {
  const n = normalizeSuggestQuery(text);
  const q = normalizeSuggestQuery(query);
  if (!n || !q) return false;
  if (n.includes(q)) return true;
  const words = n.split(/\s+/).filter(Boolean);
  if (words.some((w) => w.startsWith(q))) return true;
  if (q.length >= 2 && words.some((w) => w.includes(q))) return true;
  const parts = q.split(/\s+/).filter(Boolean);
  if (parts.length > 1) {
    return parts.every(
      (p) =>
        n.includes(p) ||
        words.some((w) => w.startsWith(p) || (p.length >= 2 && w.includes(p)))
    );
  }
  return false;
}

function scoreSuggestion(item, query, routeContext) {
  const q = normalizeSuggestQuery(query);
  const textLower = normalizeSuggestQuery(item.text);
  let score = 0;

  if (!q) {
    if (item.source === 'recent') score += 120;
    if (item.source === 'entity') score += 95;
    if (item.source === 'tool-query') score += 88;
    if (item.source === 'tool') score += 70;
    if (Array.isArray(item.routes) && item.routes.includes(routeContext)) score += 35;
    score += item.popularity || 0;
    return score;
  }

  if (item.source === 'database' && q.length >= 2) {
    if (textLower.startsWith(q)) return 250;
    if (textMatchesQuery(item.text, q)) return 220;
    return 0;
  }

  if (item.source === 'tool-query') {
    if (textLower.includes(q)) return 195;
    if (textLower.split(/\s+/).some((w) => w.startsWith(q))) return 170;
    if (q.length >= 2 && textLower.split(/\s+/).some((w) => w.includes(q))) return 160;
    return 0;
  }

  if (item.source === 'entity' && q.length >= 2) {
    const words = textLower.split(/\s+/);
    if (textLower.startsWith(q)) return 240;
    if (words.some((w) => w.startsWith(q))) return 200;
    if (textLower.includes(q)) return 180;
    if (q.length >= 2 && words.some((w) => w.includes(q))) return 170;
    return 0;
  }

  if (textLower.startsWith(q)) score += 200;

  const words = textLower.split(/\s+/);
  if (words.some((w) => w.startsWith(q))) score += 150;

  const keywords = Array.isArray(item.keywords) ? item.keywords : [];
  if (keywords.some((k) => k.startsWith(q))) score += 130;

  if (q.length >= 3 && textLower.includes(q)) score += 80;
  if (q.length >= 3 && keywords.some((k) => k.includes(q))) score += 50;

  if (item.source === 'recent') {
    if (textLower.startsWith(q) || words.some((w) => w.startsWith(q))) score += 40;
  }

  if (Array.isArray(item.routes) && item.routes.includes(routeContext)) score += 15;

  return score;
}

/**
 * @param {{ query?: string, route?: object, recentMessages?: string[], entityHints?: object[], limit?: number }} opts
 * @returns {{ id: string, text: string, category: string, entityType?: string, fillText?: string }[]}
 */
export function getQuerySuggestions(opts = {}) {
  const query = typeof opts.query === 'string' ? opts.query : '';
  const limit = Number.isFinite(opts.limit) ? opts.limit : 8;
  const routeContext = inferAssistantRouteContext(opts.route);
  const recent = Array.isArray(opts.recentMessages) ? opts.recentMessages : [];
  const entityHints = Array.isArray(opts.entityHints) ? opts.entityHints : [];

  const seen = new Set();
  const pool = [];

  const entityRows = [];
  for (const row of entityHints) {
    const text = typeof row?.text === 'string' ? row.text.trim() : '';
    if (!text || seen.has(text.toLowerCase())) continue;
    seen.add(text.toLowerCase());
    const entry = {
      id: row.id || `entity-${pool.length}`,
      text,
      fillText: row.fillText || text,
      category: row.category || 'Match',
      entityType: row.entityType || null,
      source: 'entity',
      popularity: 88,
      routes: [],
      keywords: [],
    };
    pool.push(entry);
    entityRows.push(entry);
  }

  for (const row of buildEntityToolQuerySuggestions(entityRows, query, routeContext)) {
    if (seen.has(row.text.toLowerCase())) continue;
    seen.add(row.text.toLowerCase());
    pool.push(row);
  }

  for (const row of getToolQuerySuggestionPool(routeContext)) {
    if (seen.has(row.text.toLowerCase())) continue;
    seen.add(row.text.toLowerCase());
    pool.push(row);
  }

  for (const msg of recent) {
    const text = typeof msg === 'string' ? msg.trim() : '';
    if (!text || text.length < 4 || seen.has(text.toLowerCase())) continue;
    seen.add(text.toLowerCase());
    pool.push({
      id: `recent-${pool.length}`,
      text,
      category: 'Recent',
      source: 'recent',
      popularity: 0,
      routes: [],
      keywords: [],
    });
  }

  const q = query.trim();
  const minScore = q.length >= 1 ? 100 : 0;

  return pool
    .map((item) => ({ ...item, score: scoreSuggestion(item, q, routeContext) }))
    .filter((item) => item.score >= minScore)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ id, text, fillText, category, entityType }) => ({
      id,
      text,
      fillText: fillText || text,
      category,
      entityType,
    }));
}

/** Merge suggestion lists; first list wins on duplicate text. */
export function mergeAssistantSuggestions(...lists) {
  const seen = new Set();
  const out = [];
  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      const text = typeof item?.text === 'string' ? item.text.trim() : '';
      if (!text) continue;
      const key = text.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
  }
  return out;
}

/** Text to insert when user picks an entity suggestion (screen-aware). */
export function entitySuggestionFillText(entityType, name, route) {
  const n = String(name || '').trim();
  if (!n) return '';
  const ctx = inferAssistantRouteContext(route);
  if (entityType === 'customer') {
    if (ctx === 'consultant') return `customer ${n}`;
    return `customer ${n}`;
  }
  if (entityType === 'vendor') {
    if (ctx === 'vendor') return `vendor ${n}`;
    return `vendor ${n}`;
  }
  if (entityType === 'consultant') return `consultant ${n}`;
  if (entityType === 'timesheet') {
    if (ctx === 'consultant' || ctx === 'vendor') return `show timesheets for ${n}`;
    return `timesheet ${n}`;
  }
  return n;
}
